// Per-client detail: header, allowlist editor, invite, portal-users card
// (team roles + admin-only API-key-access grant), reporting tokens, and the
// per-client audit-log viewer.
//
// SECURITY: the allowlist edited here is THE server-side source of truth the
// proxy enforces on EVERY request — a client can only ever reach account IDs
// on this list and cannot self-grant access. Save uses REPLACE semantics: the
// whole list is overwritten with what's in the textarea. The audit rows are
// already sanitized server-side; we never reconstruct upstream secrets. Errors
// are surfaced from the sanitized ApiError.message only.
//
// CHROME: NEXOVA AdminShell + nexus primitives (Card sections, DataTable,
// ToneBadge/StatusPill, Modal). The data hooks, API calls, parse logic, and
// form behavior are unchanged from the legacy screen.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { CopyIcon } from "lucide-react";

import { api, type ApiError } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import { TelegramChatBinding } from "@/components/TelegramChatBinding";
import {
  AddressBlock,
  DataTable,
  MoneyCell,
  Modal,
  StatusPill,
  ToneBadge,
  type Column,
} from "@/components/nexus";
import { addCents, subCents } from "@/routes/nexus/money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface Client {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  // Unix seconds (the admin backend serializes c.CreatedAt.Unix()).
  created_at: number;
}

interface AllowlistEntry {
  account_id: string;
  created_at: string;
}

interface Allowlist {
  client_id: string;
  account_ids: AllowlistEntry[];
}

interface AuditRecord {
  id: string;
  client_id: string;
  api_key_id: string;
  method: string;
  path: string;
  account_id: string;
  tiktok_status: number;
  latency_ms: number;
  timestamp: number;
}

interface AuditLog {
  client_id: string;
  records: AuditRecord[];
}

interface PortalUser {
  user_id: string;
  email: string;
  team_role: string;
  api_keys_access: boolean;
  totp_enabled: boolean;
  // Offboarding flag (F4): false = deactivated (sessions revoked, login
  // refused). The admin surface has no self/last-owner guard.
  active: boolean;
  // RFC3339 string (the backend serializes time.Time directly here).
  created_at: string;
}

// POST /admin/clients/{id}/users/{uid}/reset response (F3). The temporary
// password appears ONLY here — never logged, never audited, never cached.
interface CredentialReset {
  temporary_password: string;
  totp_reset: boolean;
  sessions_revoked: number;
}

interface PortalUserList {
  users: PortalUser[];
}

// Reporting tokens (Google Sheets balance integration). The SAFE list/metadata
// shape — NEVER carries the raw token or its hash. created_at is Unix seconds
// (the backend serializes reportingKeyMeta.CreatedAt as an int64).
interface ReportingKeyMeta {
  id: string;
  prefix: string;
  name: string;
  enabled: boolean;
  created_at: number;
}

interface ReportingKeyList {
  reporting_keys: ReportingKeyMeta[];
}

// Assigned deposit wallet(s) — GET /admin/clients/{id}/wallets returns
// { wallets: [{ chain, address }] } (ETH in EIP-55 display form; empty when
// none assigned). The browser holds NO credentials — this is a display-only
// watch-only deposit address (invariant 1).
interface AssignedWallet {
  chain: "tron" | "eth";
  address: string;
}
interface WalletList {
  wallets: AssignedWallet[];
}

// Ledger-derived balance — GET /admin/clients/{id}/balance returns the four
// aggregate figures as EXACT decimal strings (zero float64). An existing client
// with no ledger rows returns zeros, not a 404.
interface ClientBalance {
  client_id: string;
  total_credit: string;
  total_debit: string;
  balance: string;
  gross_topup: string;
}

// One ledger row — GET /admin/ledger/entries?client_id={id}. We bucket by `type`
// for the 3-way breakdown (top-ups / account top-up-request deductions /
// adjustments). Money fields are exact decimal strings.
interface LedgerEntry {
  id: number;
  type: string;
  debit: string;
  credit: string;
  is_manual: boolean;
  entry_date: string;
  created_at: number;
  reason: string;
}
interface LedgerList {
  entries: LedgerEntry[];
}

// The create response — the ONLY shape that carries the raw token, returned
// EXACTLY ONCE. created_at here is an RFC3339 string (the backend serializes
// reportingKeyCreatedResponse.CreatedAt as a time.Time). We never persist
// reporting_key beyond the one-time reveal callout.
interface ReportingKeyCreated {
  id: string;
  prefix: string;
  name: string;
  reporting_key: string;
  enabled: boolean;
  created_at: string;
}

const AUDIT_LIMIT = 50;
// Pull a generous page of ledger rows for the per-type breakdown buckets. The
// headline figures come from /balance (always reconcile to the view); this page
// is only for the 3-way split.
const LEDGER_LIMIT = 500;

// Bucket ledger rows by `type` and sum credit/debit (exact-decimal cents math,
// zero float64). Returns the three breakdown figures as 2dp strings:
//   topups       = Σ credit of type 'client_topup'
//   deductions   = Σ debit  of type 'account_topup' (recharge-request done debits)
//   adjustments  = Σ (credit − debit) of type 'manual_adjustment' | 'reversal'
function computeBreakdown(entries: LedgerEntry[]): {
  topups: string;
  deductions: string;
  adjustments: string;
} {
  let topups = "0.00";
  let deductions = "0.00";
  let adjustments = "0.00";
  for (const e of entries) {
    if (e.type === "client_topup") {
      topups = addCents(topups, e.credit || "0.00");
    } else if (e.type === "account_topup") {
      deductions = addCents(deductions, e.debit || "0.00");
    } else if (e.type === "manual_adjustment" || e.type === "reversal") {
      // Signed: a manual credit raises, a manual debit lowers.
      adjustments = addCents(adjustments, e.credit || "0.00");
      adjustments = subCents(adjustments, e.debit || "0.00");
    }
  }
  return { topups, deductions, adjustments };
}

const inviteSchema = z.object({
  email: z.string().email("Enter a valid email address"),
});

type InviteValues = z.infer<typeof inviteSchema>;

// The reporting-token name is OPTIONAL (a human label like "Gmart sheet"); the
// backend accepts an empty/omitted name. We just cap the length client-side.
const reportingKeySchema = z.object({
  name: z.string().max(120, "Keep the name under 120 characters").optional(),
});

type ReportingKeyValues = z.infer<typeof reportingKeySchema>;

// Normalize a messy paste (a spreadsheet column, comma list, etc.) into a clean,
// unique list of numeric account IDs, preserving first-seen order. We split on
// any non-digit run — newlines, commas, semicolons, tabs, or spaces — so the
// admin can paste straight from a sheet and only well-formed numeric advertiser
// IDs survive. Blanks, duplicates, and non-numeric junk are dropped.
//
// TikTok advertiser_ids are numeric strings; the SET write is REPLACE semantics,
// so the cleaned result is exactly what becomes the client's whole allowlist.
function parseAllowlist(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  // Split on anything that is not a digit; "" tokens from leading/trailing/
  // repeated separators are filtered by the numeric guard below.
  for (const raw of text.split(/[^0-9]+/)) {
    const id = raw.trim();
    if (id && /^[0-9]+$/.test(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

export function ClientDetailPage() {
  const { id = "" } = useParams();

  // Header
  const [client, setClient] = useState<Client | null>(null);
  const [clientLoading, setClientLoading] = useState(true);
  const [clientError, setClientError] = useState<string | null>(null);

  // Allowlist
  const [allowlistText, setAllowlistText] = useState("");
  const [allowlistLoading, setAllowlistLoading] = useState(true);
  const [allowlistError, setAllowlistError] = useState<string | null>(null);
  const [savingAllowlist, setSavingAllowlist] = useState(false);

  // Users (team roles, F6)
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  // The user pending an access-toggle confirmation, or null when the confirm
  // dialog is closed. The grant gates the client's whole API-key surface, so
  // both directions are confirm-gated (same pattern as client disable).
  const [confirmAccess, setConfirmAccess] = useState<PortalUser | null>(null);
  const [accessBusy, setAccessBusy] = useState(false);
  // The user pending an active-flag flip confirmation (deactivate/reactivate).
  const [confirmActive, setConfirmActive] = useState<PortalUser | null>(null);
  const [activeBusy, setActiveBusy] = useState(false);
  // The user pending a credential-reset confirmation.
  const [confirmReset, setConfirmReset] = useState<PortalUser | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  // The one-time temporary password from the most recent reset. Held ONLY in
  // component state for the single reveal (like the reporting-token reveal);
  // never persisted anywhere, cleared when the modal closes.
  const [resetResult, setResetResult] = useState<
    { email: string; reset: CredentialReset } | null
  >(null);

  // Audit
  const [audit, setAudit] = useState<AuditRecord[]>([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Invite
  const inviteForm = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "" },
  });

  // Reporting tokens (Google Sheets balance integration).
  const [reportingKeys, setReportingKeys] = useState<ReportingKeyMeta[]>([]);
  const [reportingLoading, setReportingLoading] = useState(true);
  const [reportingError, setReportingError] = useState<string | null>(null);
  // The "Issue token" form dialog open state.
  const [issueOpen, setIssueOpen] = useState(false);
  // The raw token from the most recent create, shown EXACTLY ONCE in a callout.
  // Held only in component state for the one-time reveal; never persisted, never
  // written to localStorage, and cleared on dismiss.
  const [newReportingKey, setNewReportingKey] = useState<ReportingKeyCreated | null>(
    null,
  );
  // The key pending a revoke confirmation, or null when the dialog is closed.
  const [confirmRevoke, setConfirmRevoke] = useState<ReportingKeyMeta | null>(
    null,
  );
  const [reportingBusy, setReportingBusy] = useState(false);

  const reportingForm = useForm<ReportingKeyValues>({
    resolver: zodResolver(reportingKeySchema),
    defaultValues: { name: "" },
  });

  // Deposit wallets (assigned, watch-only).
  const [wallets, setWallets] = useState<AssignedWallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [walletsError, setWalletsError] = useState<string | null>(null);

  // Ledger-derived balance + the 3-way breakdown.
  const [balance, setBalance] = useState<ClientBalance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);

  async function loadClient() {
    setClientLoading(true);
    setClientError(null);
    try {
      const res = await api.get<Client>(`/admin/clients/${id}`);
      setClient(res);
    } catch (err) {
      setClientError(errMessage(err));
    } finally {
      setClientLoading(false);
    }
  }

  async function loadAllowlist() {
    setAllowlistLoading(true);
    setAllowlistError(null);
    try {
      const res = await api.get<Allowlist>(`/admin/clients/${id}/allowlist`);
      setAllowlistText(res.account_ids.map((a) => a.account_id).join("\n"));
    } catch (err) {
      setAllowlistError(errMessage(err));
    } finally {
      setAllowlistLoading(false);
    }
  }

  async function loadUsers() {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await api.get<PortalUserList>(`/admin/clients/${id}/users`);
      setUsers(res.users);
    } catch (err) {
      setUsersError(errMessage(err));
    } finally {
      setUsersLoading(false);
    }
  }

  async function loadReportingKeys() {
    setReportingLoading(true);
    setReportingError(null);
    try {
      const res = await api.get<ReportingKeyList>(
        `/admin/clients/${id}/reporting-keys`,
      );
      setReportingKeys(res.reporting_keys);
    } catch (err) {
      setReportingError(errMessage(err));
    } finally {
      setReportingLoading(false);
    }
  }

  async function loadWallets() {
    setWalletsLoading(true);
    setWalletsError(null);
    try {
      const res = await api.get<WalletList>(`/admin/clients/${id}/wallets`);
      setWallets(res.wallets ?? []);
    } catch (err) {
      setWalletsError(errMessage(err));
    } finally {
      setWalletsLoading(false);
    }
  }

  // Load the headline balance AND the ledger entries used for the breakdown. The
  // ledger fetch is tolerant: a failed ledger leaves the breakdown empty but the
  // headline balance still renders.
  async function loadBalance() {
    setBalanceLoading(true);
    setBalanceError(null);
    try {
      const res = await api.get<ClientBalance>(`/admin/clients/${id}/balance`);
      setBalance(res);
      const led = await api
        .get<LedgerList>(
          `/admin/ledger/entries?client_id=${id}&limit=${LEDGER_LIMIT}`,
        )
        .catch(() => null);
      setLedger(led?.entries ?? []);
    } catch (err) {
      setBalanceError(errMessage(err));
    } finally {
      setBalanceLoading(false);
    }
  }

  async function loadAudit(nextOffset: number) {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const res = await api.get<AuditLog>(
        `/admin/clients/${id}/audit?limit=${AUDIT_LIMIT}&offset=${nextOffset}`,
      );
      setAudit(res.records);
    } catch (err) {
      setAuditError(errMessage(err));
    } finally {
      setAuditLoading(false);
    }
  }

  useEffect(() => {
    void loadClient();
    void loadAllowlist();
    void loadUsers();
    void loadReportingKeys();
    void loadWallets();
    void loadBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    void loadAudit(offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, offset]);

  async function saveAllowlist() {
    setSavingAllowlist(true);
    try {
      const account_ids = parseAllowlist(allowlistText);
      await api.put(`/admin/clients/${id}/allowlist`, { account_ids });
      toast.success("Allowlist updated");
      await loadAllowlist();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSavingAllowlist(false);
    }
  }

  async function onInvite(values: InviteValues) {
    try {
      await api.post("/admin/invites", {
        email: values.email,
        client_id: id,
        role: "client",
      });
      toast.success("Invite sent");
      inviteForm.reset();
    } catch (err) {
      // The backend pre-checks portal_users and 409s on a duplicate email —
      // map it to a friendly message instead of the raw sanitized error.
      toast.error(
        (err as ApiError)?.status === 409
          ? "That email already has an account."
          : errMessage(err),
      );
    }
  }

  // Flip the admin-only api_keys_access grant for one portal user. The flag is
  // DB-resolved on every portal request server-side, so a revoke bites on the
  // user's very next call; the UI just reflects it.
  async function setApiKeyAccess(u: PortalUser) {
    setAccessBusy(true);
    try {
      await api.post(
        `/admin/clients/${id}/users/${u.user_id}/api-key-access`,
        { allowed: !u.api_keys_access },
      );
      toast.success(
        u.api_keys_access ? "API-key access revoked" : "API-key access granted",
      );
      setConfirmAccess(null);
      await loadUsers();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setAccessBusy(false);
    }
  }

  // Flip a portal user's active flag (F4 offboarding). Deactivation also
  // revokes the user's sessions server-side (invariant 4 — their next call
  // 401s even with a live access token); reactivation restores access but does
  // NOT resurrect old sessions.
  async function setUserActive(u: PortalUser) {
    setActiveBusy(true);
    try {
      await api.post(`/admin/clients/${id}/users/${u.user_id}/active`, {
        active: !u.active,
      });
      toast.success(
        u.active
          ? "User deactivated — sessions revoked"
          : "User reactivated — they must log in again",
      );
      setConfirmActive(null);
      await loadUsers();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setActiveBusy(false);
    }
  }

  // Reset a portal user's credentials (F3): the backend mints a one-time
  // temporary password, wipes their TOTP (fresh QR at next login), and revokes
  // every live session. The temporary password is shown EXACTLY ONCE.
  async function resetCredentials(u: PortalUser) {
    setResetBusy(true);
    try {
      const res = await api.post<CredentialReset>(
        `/admin/clients/${id}/users/${u.user_id}/reset`,
      );
      setConfirmReset(null);
      setResetResult({ email: u.email, reset: res });
      await loadUsers();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setResetBusy(false);
    }
  }

  // Issue a new reporting token. The raw token comes back EXACTLY ONCE: we stash
  // it in newReportingKey only to render the one-time reveal callout, then reload
  // the (token-free) list. The issue form dialog closes; the reveal callout stays
  // until the admin dismisses it.
  async function onIssueReportingKey(values: ReportingKeyValues) {
    try {
      const name = values.name?.trim() ?? "";
      const res = await api.post<ReportingKeyCreated>(
        `/admin/clients/${id}/reporting-keys`,
        name ? { name } : {},
      );
      setNewReportingKey(res);
      setIssueOpen(false);
      reportingForm.reset();
      toast.success("Reporting token issued");
      await loadReportingKeys();
    } catch (err) {
      toast.error(errMessage(err));
    }
  }

  // Revoke (disable) a reporting token — fast per-key revocation. The disabled
  // token fails on its very next /reporting/* call; the UI reflects it on reload.
  async function revokeReportingKey(k: ReportingKeyMeta) {
    setReportingBusy(true);
    try {
      await api.post(`/admin/clients/${id}/reporting-keys/${k.id}/revoke`);
      toast.success("Reporting token revoked");
      setConfirmRevoke(null);
      await loadReportingKeys();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setReportingBusy(false);
    }
  }

  // Copy the one-time raw token to the clipboard. Best-effort: if the Clipboard
  // API is unavailable the admin can still select the visible value manually.
  async function copyReportingKey(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — select and copy it manually.");
    }
  }

  const userColumns: Column<PortalUser>[] = [
    {
      key: "email",
      header: "Email",
      cell: (u) => <span className="text-nx-text">{u.email}</span>,
    },
    {
      key: "role",
      header: "Role",
      cell: (u) => (
        <ToneBadge tone="brand" className="capitalize">
          {u.team_role}
        </ToneBadge>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (u) => (
        <StatusPill tone={u.active ? "credit" : "danger"}>
          {u.active ? "active" : "deactivated"}
        </StatusPill>
      ),
    },
    {
      key: "2fa",
      header: "2FA",
      cell: (u) => (
        <StatusPill tone={u.totp_enabled ? "credit" : "na"}>
          {u.totp_enabled ? "2FA on" : "2FA off"}
        </StatusPill>
      ),
    },
    {
      key: "apikeys",
      header: "API keys",
      cell: (u) => (
        <StatusPill tone={u.api_keys_access ? "credit" : "na"}>
          {u.api_keys_access ? "granted" : "no access"}
        </StatusPill>
      ),
    },
    {
      key: "created",
      header: "Created",
      cell: (u) => (
        <span className="text-sm text-nx-text-2 tabular-nums">
          {new Date(u.created_at).toLocaleString()}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (u) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant={u.api_keys_access ? "destructive" : "outline"}
            size="sm"
            disabled={accessBusy}
            onClick={() => setConfirmAccess(u)}
          >
            {u.api_keys_access ? "Revoke API keys" : "Grant API keys"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={resetBusy}
            onClick={() => setConfirmReset(u)}
          >
            Reset credentials
          </Button>
          <Button
            variant={u.active ? "destructive" : "outline"}
            size="sm"
            disabled={activeBusy}
            onClick={() => setConfirmActive(u)}
          >
            {u.active ? "Deactivate" : "Reactivate"}
          </Button>
        </div>
      ),
    },
  ];

  const reportingColumns: Column<ReportingKeyMeta>[] = [
    {
      key: "name",
      header: "Name",
      cell: (k) =>
        k.name ? (
          <span className="text-nx-text">{k.name}</span>
        ) : (
          <span className="text-nx-text-3">(unnamed)</span>
        ),
    },
    {
      key: "prefix",
      header: "Prefix",
      cell: (k) => (
        <code className="font-mono text-sm text-nx-text-2">{k.prefix}…</code>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (k) => (
        <StatusPill tone={k.enabled ? "credit" : "danger"}>
          {k.enabled ? "active" : "revoked"}
        </StatusPill>
      ),
    },
    {
      key: "created",
      header: "Created",
      cell: (k) => (
        <span className="text-sm text-nx-text-2 tabular-nums">
          {new Date(k.created_at * 1000).toLocaleString()}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (k) =>
        k.enabled ? (
          <Button
            variant="destructive"
            size="sm"
            disabled={reportingBusy}
            onClick={() => setConfirmRevoke(k)}
          >
            Revoke
          </Button>
        ) : (
          <span className="text-sm text-nx-text-3">revoked</span>
        ),
    },
  ];

  const auditColumns: Column<AuditRecord>[] = [
    {
      key: "time",
      header: "Time",
      cell: (r) => (
        <span className="text-sm text-nx-text-2 tabular-nums">
          {new Date(r.timestamp * 1000).toLocaleString()}
        </span>
      ),
    },
    {
      key: "method",
      header: "Method",
      cell: (r) => <span className="text-nx-text-2">{r.method}</span>,
    },
    {
      key: "path",
      header: "Path",
      cell: (r) => (
        <code className="font-mono text-xs text-nx-text-2">{r.path}</code>
      ),
    },
    {
      key: "account",
      header: "Account",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          {r.account_id}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => (
        <span className="tabular-nums text-nx-text-2">{r.tiktok_status}</span>
      ),
    },
    {
      key: "latency",
      header: "Latency",
      align: "right",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          {r.latency_ms}ms
        </span>
      ),
    },
  ];

  const breakdown = computeBreakdown(ledger);

  // Proxy readiness (onboarding). A client can only proxy TikTok API calls once
  // (a) at least one active portal user has the admin-granted api_keys_access
  // flag AND (b) at least one advertiser ID is on the allowlist. With the
  // default-on link⇒allowlist (ADR-0051) the allowlist usually fills on
  // attribution, so the residual gap is almost always api_keys_access.
  const allowlistCount = parseAllowlist(allowlistText).length;
  const apiKeyAccessGranted = users.some(
    (u) => u.api_keys_access && u.active !== false,
  );
  const proxyReady = apiKeyAccessGranted && allowlistCount > 0;
  // The first active user still lacking access — the target of the inline grant.
  const grantTarget =
    users.find((u) => !u.api_keys_access && u.active !== false) ?? null;
  const readinessLoading = usersLoading || allowlistLoading;

  return (
    <AdminShell>
      <div className="grid gap-6">
        {/* Header card */}
        <Card>
          <CardHeader>
            <CardTitle>Client</CardTitle>
          </CardHeader>
          <CardContent>
            {clientError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not load client</AlertTitle>
                <AlertDescription>{clientError}</AlertDescription>
              </Alert>
            ) : clientLoading || !client ? (
              <div className="grid gap-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
            ) : (
              <div className="grid gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium text-nx-text">
                    {client.name}
                  </span>
                  <StatusPill tone={client.enabled ? "credit" : "danger"}>
                    {client.enabled ? "enabled" : "disabled"}
                  </StatusPill>
                </div>
                <div className="text-sm text-nx-text-2">{client.email}</div>
                <div className="text-sm text-nx-text-3">
                  Created {new Date(client.created_at * 1000).toLocaleString()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Proxy readiness (onboarding) — what this client still needs before
            it can proxy TikTok API calls. */}
        <Card>
          <CardHeader>
            <CardTitle>Proxy readiness</CardTitle>
            <CardDescription>
              Before this client can proxy TikTok API calls it needs a user with
              API-key access and at least one allowlisted account.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {readinessLoading ? (
              <div className="grid gap-2">
                <Skeleton className="h-6 w-56" />
                <Skeleton className="h-6 w-56" />
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-nx-text-2">
                      API-key access:
                    </span>
                    <StatusPill tone={apiKeyAccessGranted ? "credit" : "warn"}>
                      {apiKeyAccessGranted ? "granted" : "not granted"}
                    </StatusPill>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-nx-text-2">
                      Allowlisted accounts:
                    </span>
                    <StatusPill tone={allowlistCount > 0 ? "credit" : "warn"}>
                      {allowlistCount}
                    </StatusPill>
                  </div>
                  {proxyReady && <ToneBadge tone="credit">Proxy-ready</ToneBadge>}
                </div>

                {!proxyReady && (
                  <div className="grid gap-2 rounded-md bg-nx-warn-soft/60 px-3 py-2">
                    <p className="text-xs font-medium text-nx-warn">
                      Not yet proxy-ready
                    </p>
                    <p className="text-xs text-nx-text-2">
                      {!apiKeyAccessGranted && allowlistCount === 0
                        ? "No user has API-key access and no accounts are allowlisted yet."
                        : !apiKeyAccessGranted
                          ? "No user has API-key access yet — grant a user access to unlock this client's key surface."
                          : "No accounts are allowlisted yet — add at least one advertiser ID in the Allowlist section below."}
                    </p>
                    {!apiKeyAccessGranted && grantTarget && (
                      <div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={accessBusy}
                          onClick={() => setConfirmAccess(grantTarget)}
                        >
                          Grant a user access
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Telegram chat card (Wave 9.3 ops-bot binding) */}
        <Card>
          <CardHeader>
            <CardTitle>Telegram chat</CardTitle>
            <CardDescription>
              The client's Telegram group for the ops bot. Binding is gated on
              a live check that the bot is actually usable in the chat; the
              backend re-validates on save.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TelegramChatBinding kind="client" ownerId={id} />
          </CardContent>
        </Card>

        {/* Deposit wallets card (assigned, watch-only) */}
        <Card>
          <CardHeader>
            <CardTitle>Deposit wallets</CardTitle>
            <CardDescription>
              The client's assigned watch-only deposit addresses (admin-provisioned,
              xpub-derived). Display only — no signing key is ever held by the
              browser or the portal.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {walletsError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load deposit wallets</AlertTitle>
                <AlertDescription>{walletsError}</AlertDescription>
              </Alert>
            )}
            {walletsLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : wallets.length === 0 ? (
              <p className="text-sm text-nx-text-3">
                No deposit wallet assigned.
              </p>
            ) : (
              <div className="grid gap-2">
                {wallets.map((w) => (
                  <div
                    key={`${w.chain}:${w.address}`}
                    className="flex items-center justify-between gap-3 rounded-lg border border-nx-border bg-nx-bg-2 px-3 py-2"
                  >
                    <ToneBadge tone="brand" className="uppercase">
                      {w.chain}
                    </ToneBadge>
                    <AddressBlock address={w.address} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Balance card (ledger-derived) */}
        <Card>
          <CardHeader>
            <CardTitle>Balance</CardTitle>
            <CardDescription>
              The client's ledger-derived balance (Σ credits − Σ debits) with a
              breakdown by entry type. All figures are exact decimal amounts.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {balanceError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load balance</AlertTitle>
                <AlertDescription>{balanceError}</AlertDescription>
              </Alert>
            )}
            {balanceLoading || !balance ? (
              <div className="grid gap-2">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                  <div>
                    <div className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
                      Balance
                    </div>
                    <div className="text-2xl font-semibold tabular-nums text-nx-text">
                      <MoneyCell value={balance.balance} />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
                      Total credit
                    </div>
                    <MoneyCell value={balance.total_credit} tone="credit" />
                  </div>
                  <div>
                    <div className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
                      Total debit
                    </div>
                    <MoneyCell value={balance.total_debit} tone="debit" />
                  </div>
                  <div>
                    <div className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
                      Gross top-up
                    </div>
                    <MoneyCell value={balance.gross_topup} tone="credit" />
                  </div>
                </div>

                <div className="grid gap-2 rounded-lg border border-nx-border bg-nx-bg-2 p-3">
                  <div className="text-xs font-medium tracking-wide text-nx-text-3 uppercase">
                    Breakdown by entry type
                  </div>
                  <div className="flex items-center justify-between border-b border-nx-border py-1.5 text-sm">
                    <span className="text-nx-text-2">Top-ups (credits)</span>
                    <MoneyCell value={breakdown.topups} tone="credit" signed="+" />
                  </div>
                  <div className="flex items-center justify-between border-b border-nx-border py-1.5 text-sm">
                    <span className="text-nx-text-2">
                      Account top-up-request deductions (debits)
                    </span>
                    <MoneyCell value={breakdown.deductions} tone="debit" signed="-" />
                  </div>
                  <div className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-nx-text-2">
                      Adjustments (manual / reversal)
                    </span>
                    <MoneyCell value={breakdown.adjustments} />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Allowlist card */}
        <Card>
          <CardHeader>
            <CardTitle>Allowlist</CardTitle>
            <CardDescription>
              The account IDs (TikTok advertiser IDs) this client is allowed to
              reach. This is how you link ad accounts to a client — paste a
              spreadsheet column or a comma list; only numeric IDs are kept and
              duplicates/blanks are dropped. Saving REPLACES the entire allowlist
              for this client.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {allowlistError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load allowlist</AlertTitle>
                <AlertDescription>{allowlistError}</AlertDescription>
              </Alert>
            )}
            {allowlistLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <textarea
                aria-label="Allowlist account IDs"
                className="min-h-32 w-full rounded-lg border border-nx-border bg-nx-bg-2 px-2.5 py-2 font-mono text-sm text-nx-text outline-none focus-visible:border-nx-brand focus-visible:ring-3 focus-visible:ring-nx-brand/20"
                value={allowlistText}
                onChange={(e) => setAllowlistText(e.target.value)}
              />
            )}
            <p className="text-xs text-nx-text-3">
              Will save {parseAllowlist(allowlistText).length} unique account
              {parseAllowlist(allowlistText).length === 1 ? "" : "s"}. Reporting
              tokens always read this live list, so newly added accounts appear
              automatically.
            </p>
            <div>
              <Button
                onClick={saveAllowlist}
                disabled={savingAllowlist || allowlistLoading}
              >
                Save
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Invite card */}
        <Card>
          <CardHeader>
            <CardTitle>Invite</CardTitle>
            <CardDescription>
              Invite a user for this client. In dev the invite link is in the
              ttp-admin logs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...inviteForm}>
              <form
                onSubmit={inviteForm.handleSubmit(onInvite)}
                className="flex items-start gap-2"
                noValidate
              >
                <FormField
                  control={inviteForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="mt-6"
                  disabled={inviteForm.formState.isSubmitting}
                >
                  Send invite
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Users card (team roles, F6) */}
        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>
              Portal users for this client. API-key access is admin-granted
              only — off by default for everyone, including the owner.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {usersError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load users</AlertTitle>
                <AlertDescription>{usersError}</AlertDescription>
              </Alert>
            )}
            {usersLoading ? (
              <div className="grid gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <DataTable
                columns={userColumns}
                rows={users}
                rowKey={(u) => u.user_id}
                empty="No portal users yet. Send an invite to add the owner."
              />
            )}
          </CardContent>
        </Card>

        {/* Reporting tokens card (Google Sheets balance integration) */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle>Reporting tokens</CardTitle>
                <CardDescription>
                  Read-only tokens for the client's Google Sheets balance
                  integration. A token is linked to this client and always
                  reflects the client's CURRENT allowlist — newly added accounts
                  appear automatically, no re-issue needed. The raw token is
                  shown only once, at creation.
                </CardDescription>
              </div>
              <Button
                size="sm"
                onClick={() => setIssueOpen(true)}
                disabled={reportingBusy}
              >
                Issue token
              </Button>
            </div>
          </CardHeader>
          <CardContent className="grid gap-3">
            {reportingError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load reporting tokens</AlertTitle>
                <AlertDescription>{reportingError}</AlertDescription>
              </Alert>
            )}

            {/* One-time raw-token reveal. Rendered ONLY right after a create and
                cleared on dismiss; the value is never persisted or re-fetchable. */}
            {newReportingKey && (
              <Alert>
                <AlertTitle>
                  Copy this token now — it will not be shown again
                </AlertTitle>
                <AlertDescription className="grid gap-2">
                  <span>
                    This is the only time the raw token for
                    {newReportingKey.name ? ` "${newReportingKey.name}"` : ""} is
                    shown. Store it in the client's Google Sheet now; it cannot be
                    recovered later (revoke and re-issue if lost).
                  </span>
                  <div className="flex items-center gap-2">
                    <code
                      aria-label="Raw reporting token"
                      className="flex-1 overflow-x-auto rounded-md border border-nx-border bg-nx-bg-2 px-2.5 py-2 font-mono text-sm text-nx-text"
                    >
                      {newReportingKey.reporting_key}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        copyReportingKey(newReportingKey.reporting_key)
                      }
                    >
                      <CopyIcon className="size-4" />
                      Copy
                    </Button>
                  </div>
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setNewReportingKey(null)}
                    >
                      Dismiss
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {reportingLoading ? (
              <div className="grid gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <DataTable
                columns={reportingColumns}
                rows={reportingKeys}
                rowKey={(k) => k.id}
                empty="No reporting tokens yet. Issue one to connect the client's balance sheet."
              />
            )}
          </CardContent>
        </Card>

        {/* Audit card */}
        <Card>
          <CardHeader>
            <CardTitle>Audit log</CardTitle>
            <CardDescription>
              Proxied calls for this client (read-only, sanitized).
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {auditError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load audit log</AlertTitle>
                <AlertDescription>{auditError}</AlertDescription>
              </Alert>
            )}
            {auditLoading ? (
              <div className="grid gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <DataTable
                columns={auditColumns}
                rows={audit}
                rowKey={(r) => r.id}
                empty="No audit records for this page."
              />
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || auditLoading}
                onClick={() => setOffset((o) => Math.max(0, o - AUDIT_LIMIT))}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={audit.length < AUDIT_LIMIT || auditLoading}
                onClick={() => setOffset((o) => o + AUDIT_LIMIT)}
              >
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Issue reporting token dialog (optional name). */}
      <Modal
        open={issueOpen}
        onOpenChange={(open) => {
          setIssueOpen(open);
          if (!open) reportingForm.reset();
        }}
        title="Issue reporting token"
        description="Mint a read-only token for this client's balance sheet. The raw token is shown only once on the next screen — copy it before dismissing."
      >
        <Form {...reportingForm}>
          <form
            onSubmit={reportingForm.handleSubmit(onIssueReportingKey)}
            className="grid gap-4"
            noValidate
          >
            <FormField
              control={reportingForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name (optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Gmart sheet"
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIssueOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={reportingForm.formState.isSubmitting}
              >
                Issue
              </Button>
            </div>
          </form>
        </Form>
      </Modal>

      {/* Revoke reporting token confirm dialog (destructive, confirm-gated). */}
      <Modal
        open={confirmRevoke !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRevoke(null);
        }}
        title={`Revoke ${confirmRevoke?.name || "this reporting token"}?`}
        description={
          <>
            The token ({confirmRevoke?.prefix}…) stops working immediately on its
            next request. Any sheet using it will fail until you issue a new one.
            This cannot be undone.
          </>
        }
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmRevoke(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={reportingBusy}
              onClick={() => confirmRevoke && revokeReportingKey(confirmRevoke)}
            >
              Confirm
            </Button>
          </>
        }
      />

      {/* API-key access confirm dialog (same confirm-gating as client disable). */}
      <Modal
        open={confirmAccess !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAccess(null);
        }}
        title={
          confirmAccess?.api_keys_access
            ? `Revoke API-key access from ${confirmAccess?.email}?`
            : `Grant API-key access to ${confirmAccess?.email}?`
        }
        description={
          confirmAccess?.api_keys_access
            ? "They immediately lose the whole API-key surface (view, create, disable)."
            : "They can then view, create, and disable this client's proxy API keys."
        }
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmAccess(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={confirmAccess?.api_keys_access ? "destructive" : "default"}
              disabled={accessBusy}
              onClick={() => confirmAccess && setApiKeyAccess(confirmAccess)}
            >
              Confirm
            </Button>
          </>
        }
      />

      {/* Active-flag flip confirm dialog (F4 offboarding, confirm-gated). */}
      <Modal
        open={confirmActive !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmActive(null);
        }}
        title={
          confirmActive?.active
            ? `Deactivate ${confirmActive?.email}?`
            : `Reactivate ${confirmActive?.email}?`
        }
        description={
          confirmActive?.active
            ? "Their sessions are revoked immediately and login is refused until reactivated."
            : "They regain access at once but must log in again — old sessions stay dead."
        }
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmActive(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={confirmActive?.active ? "destructive" : "default"}
              disabled={activeBusy}
              onClick={() => confirmActive && setUserActive(confirmActive)}
            >
              Confirm
            </Button>
          </>
        }
      />

      {/* Credential-reset confirm dialog (F3, confirm-gated). */}
      <Modal
        open={confirmReset !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmReset(null);
        }}
        title={`Reset credentials for ${confirmReset?.email}?`}
        description={
          <>
            Their current password, authenticator (TOTP) and every live session
            die immediately. You will get a one-time temporary password to hand
            to them out-of-band; they will scan a fresh TOTP QR at next login.
          </>
        }
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmReset(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={resetBusy}
              onClick={() => confirmReset && resetCredentials(confirmReset)}
            >
              Reset credentials
            </Button>
          </>
        }
      />

      {/* One-time temporary-password reveal — shown EXACTLY ONCE (the backend
          returns it only in the reset response; closing this modal discards
          it forever). */}
      <Modal
        open={resetResult !== null}
        onOpenChange={(open) => {
          if (!open) setResetResult(null);
        }}
        title="Temporary password — shown only once"
        description={
          resetResult
            ? `Credentials for ${resetResult.email} were reset (${resetResult.reset.sessions_revoked} session${resetResult.reset.sessions_revoked === 1 ? "" : "s"} revoked).`
            : undefined
        }
        footer={
          <Button type="button" onClick={() => setResetResult(null)}>
            Done — I copied it
          </Button>
        }
      >
        {resetResult && (
          <div className="grid gap-3">
            <div className="flex items-center gap-2 rounded-md border border-nx-border bg-nx-bg-2 px-3 py-2">
              <code
                data-testid="temporary-password"
                className="flex-1 font-mono text-sm break-all text-nx-text"
              >
                {resetResult.reset.temporary_password}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Copy temporary password"
                onClick={() =>
                  copyReportingKey(resetResult.reset.temporary_password)
                }
              >
                <CopyIcon aria-hidden className="size-3.5" />
                Copy
              </Button>
            </div>
            <p className="text-xs text-nx-text-2">
              This password is shown ONLY here — it is never stored or logged.
              Hand it to the user out-of-band; at their next login they will be
              asked to scan a fresh authenticator QR. Self-serve password change
              is a later wave.
            </p>
          </div>
        )}
      </Modal>
    </AdminShell>
  );
}
